import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { HttpClient } from '@angular/common/http';
import { environment } from '../../../environments/environment';

interface KeywordResult {
  keyword: string;
  search_volume: number;
  competition: number;
  competition_level: string;
  cpc: number;
  keyword_difficulty?: number;
  categories?: number[];
  isCovered?: boolean;
  urlCount?: number;
  matchedTopicLabel?: string;
  core_keyword?: string;
  rankingPosition?: number; // Position in SERP (1-100, lower is better)
  rankingUrl?: string; // URL that ranks for this keyword
}

interface KeywordGroup {
  groupName: string; // Seed topic (if matches) or core_keyword
  keywords: KeywordResult[];
  totalSearchVolume: number;
  totalUrlCount: number;
  isCovered: boolean; // True if any keyword in group is covered
  coveredCount: number;
  totalCount: number;
}

interface DeduplicatedKeyword {
  canonicalKeyword: string; // The normalized/canonical form (e.g., "website feedback")
  variants: KeywordResult[]; // All variants of this keyword (e.g., "website feedback", "feedback website", "feedback on website")
  totalSearchVolume: number;
  totalUrlCount: number;
  isCovered: boolean;
  coveredCount: number;
  bestRankingPosition?: number; // Best (lowest) ranking position
  bestRankingUrl?: string;
}

interface KeywordCluster {
  clusterName: string; // The cluster name (usually the canonical keyword or best matching topic)
  keywords: DeduplicatedKeyword[]; // Deduplicated keywords in this cluster
  totalSearchVolume: number;
  totalUrlCount: number;
  isCovered: boolean;
  coveredCount: number;
  totalKeywordVariants: number; // Total number of keyword variants (before deduplication)
  similarityScore?: number; // Similarity score to main topic slug (0-1, higher = more similar)
}

interface TopicKeywordResult {
  topicSlug: string;
  topicLabel: string;
  topicType: 'merged' | 'related';
  relatedKeywords: KeywordResult[];
  suggestions: KeywordResult[];
  groupedRelatedKeywords: KeywordGroup[];
  groupedSuggestions: KeywordGroup[];
  relatedKeywordsMetadata?: { dataSource: 'database' | 'api' | 'unknown'; lastUpdated: string | null };
  suggestionsMetadata?: { dataSource: 'database' | 'api' | 'unknown'; lastUpdated: string | null };
}

@Component({
  selector: 'app-keyword-research-test',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './keyword-research-test.html',
  styleUrl: './keyword-research-test.css'
})
export class KeywordResearchTestComponent implements OnInit {
  // Topic-based batch search (new section)
  domain: string = '';
  topicSlug: string = '';
  batchLoading = false;
  batchError: string | null = null;
  batchResults: TopicKeywordResult[] = [];
  allKeywords: Set<string> = new Set();
  totalCost: number = 0;
  diagnosticData: any = null; // Store diagnostic data for coverage checking
  topicSlugMap: Map<string, { urlCount: number; label: string }> = new Map(); // Map topicSlug to URL count (legacy, kept for fallback)
  rankedKeywordsMap: Map<string, { position: number; serpItem: any; url?: string }> = new Map(); // Map keyword to ranking data
  rankedKeywordsMetadata: { dataSource: 'database' | 'api' | 'unknown'; lastUpdated: string | null; keywordCount: number } | null = null; // Metadata about ranked keywords data
  expandedGroups: Set<string> = new Set(); // Track which groups are expanded (format: "topicSlug-section-groupName")
  
  // Clustered view data
  clusteredKeywords: KeywordCluster[] = [];
  filteredClusteredKeywords: KeywordCluster[] = []; // Filtered clusters (similarity >= 50%)
  showClusteredView: boolean = false; // Toggle between topic view and clustered view
  expandedClusters: Set<string> = new Set(); // Track which clusters are expanded
  similarityThreshold: number = 0.5; // Minimum similarity score to display (50%)

  // Single keyword search (existing section)
  keyword: string = '';
  method: 'related' | 'ideas' | 'suggestions' | 'keywords-for-site' = 'related';
  loading = false;
  error: string | null = null;
  results: any = null;
  resultsMetadata: { dataSource: 'database' | 'api' | 'unknown'; lastUpdated: string | null } | null = null; // Metadata about keyword research results
  locationCode: number = 2840; // United States
  languageName: string = 'English';
  depth: number = 1;
  limit: number = 100;
  filterBySearchVolume: boolean = true;
  minSearchVolume: number = 10;

  constructor(private http: HttpClient) {}

  ngOnInit() {}

  async testKeywordResearch() {
    if (!this.keyword.trim()) {
      this.error = 'Please enter a keyword';
      return;
    }

    this.loading = true;
    this.error = null;
    this.results = null;

    try {
      const API_URL = environment.apiUrl + '/v1';
      let endpoint = '';
      let body: any = {
        keyword: this.keyword,
        locationCode: this.locationCode,
        languageName: this.languageName,
        limit: this.limit
      };

      if (this.method === 'related') {
        endpoint = '/dataforseo/keyword-research/related';
        body.depth = this.depth;
        // Add search volume filter if enabled
        if (this.filterBySearchVolume && this.minSearchVolume > 0) {
          body.filters = [["keyword_data.keyword_info.search_volume", ">", this.minSearchVolume]];
        }
      } else if (this.method === 'ideas') {
        endpoint = '/dataforseo/keyword-research/ideas';
        body.keywords = [this.keyword];
        // For keyword ideas, the filter path is different (no keyword_data wrapper)
        if (this.filterBySearchVolume && this.minSearchVolume > 0) {
          body.filters = [["keyword_info.search_volume", ">", this.minSearchVolume]];
        }
      } else if (this.method === 'suggestions') {
        endpoint = '/dataforseo/keyword-research/suggestions';
        // For keyword suggestions, the filter path is keyword_info.search_volume (no keyword_data wrapper)
        if (this.filterBySearchVolume && this.minSearchVolume > 0) {
          body.filters = [["keyword_info.search_volume", ">", this.minSearchVolume]];
        }
      } else if (this.method === 'keywords-for-site') {
        endpoint = '/dataforseo/keyword-research/keywords-for-site';
        // For keywords-for-site, use target instead of keyword
        body.target = this.keyword; // Domain name
        delete body.keyword;
        // Filter path is keyword_info.search_volume
        if (this.filterBySearchVolume && this.minSearchVolume > 0) {
          body.filters = [["keyword_info.search_volume", ">", this.minSearchVolume]];
        }
      }

      // Debug: log the request body to see if filters are being sent
      console.log('Request body:', JSON.stringify(body, null, 2));

      const response = await this.http.post<any>(`${API_URL}${endpoint}`, body).toPromise();
      
      if (response.success) {
        this.results = response.data;
        // Capture metadata about data source
        this.resultsMetadata = response.metadata || {
          dataSource: 'unknown',
          lastUpdated: null
        };
        console.log(`📊 Keyword research results (source: ${this.resultsMetadata?.dataSource || 'unknown'})`);
      } else {
        this.error = response.message || 'Failed to fetch keyword research data';
        this.resultsMetadata = null;
      }
    } catch (error: any) {
      console.error('Error:', error);
      this.error = error.error?.message || error.message || 'Failed to fetch keyword research data';
      this.resultsMetadata = null;
    } finally {
      this.loading = false;
    }
  }

  getKeywordsFromResults(): KeywordResult[] {
    if (!this.results?.tasks?.[0]?.result?.[0]) {
      return [];
    }

    const result = this.results.tasks[0].result[0];
    
    if (this.method === 'related') {
      // Related keywords structure
      const items = result.items || [];
      return items.map((item: any) => ({
        keyword: item.keyword_data?.keyword || item.keyword || '',
        search_volume: item.keyword_data?.keyword_info?.search_volume || 0,
        competition: item.keyword_data?.keyword_info?.competition || 0,
        competition_level: item.keyword_data?.keyword_info?.competition_level || 'UNKNOWN',
        cpc: item.keyword_data?.keyword_info?.cpc || 0,
        keyword_difficulty: item.keyword_data?.keyword_properties?.keyword_difficulty,
        categories: item.keyword_data?.keyword_info?.categories || []
      }));
    } else if (this.method === 'ideas') {
      // Keyword ideas structure - items have keyword_info directly, not wrapped in keyword_data
      const items = result.items || [];
      return items.map((item: any) => ({
        keyword: item.keyword || '',
        search_volume: item.keyword_info?.search_volume || 0,
        competition: item.keyword_info?.competition || 0,
        competition_level: item.keyword_info?.competition_level || 'UNKNOWN',
        cpc: item.keyword_info?.cpc || 0,
        keyword_difficulty: item.keyword_properties?.keyword_difficulty,
        categories: item.keyword_info?.categories || []
      }));
    } else if (this.method === 'suggestions') {
      // Keyword suggestions structure - items have keyword_info directly, not wrapped in keyword_data
      const items = result.items || [];
      return items.map((item: any) => ({
        keyword: item.keyword || '',
        search_volume: item.keyword_info?.search_volume || 0,
        competition: item.keyword_info?.competition || 0,
        competition_level: item.keyword_info?.competition_level || 'UNKNOWN',
        cpc: item.keyword_info?.cpc || 0,
        keyword_difficulty: item.keyword_properties?.keyword_difficulty,
        categories: item.keyword_info?.categories || []
      }));
    }

    return [];
  }

  getTotalCount(): number {
    if (!this.results?.tasks?.[0]?.result?.[0]) {
      return 0;
    }
    return this.results.tasks[0].result[0].total_count || 0;
  }

  getItemsCount(): number {
    if (!this.results?.tasks?.[0]?.result?.[0]) {
      return 0;
    }
    return this.results.tasks[0].result[0].items_count || 0;
  }

  getCost(): number {
    if (!this.results?.tasks?.[0]) {
      return 0;
    }
    return this.results.tasks[0].cost || 0;
  }

  // Topic-based batch search methods
  async searchTopicsForKeywords() {
    if (!this.domain.trim() || !this.topicSlug.trim()) {
      this.batchError = 'Please enter both domain and topic slug';
      return;
    }

    this.batchLoading = true;
    this.batchError = null;
    this.batchResults = [];
    this.allKeywords.clear();
    this.totalCost = 0;

    try {
      const API_URL = environment.apiUrl + '/v1';
      
      // Step 1: Fetch diagnostic data to get merged + related topics
      const diagnosticResponse = await this.http.get<any>(
        `${API_URL}/domains/${this.domain.toLowerCase().trim()}/topics/${this.topicSlug.toLowerCase().trim()}/diagnostic`
      ).toPromise();

      if (!diagnosticResponse.success || !diagnosticResponse.data) {
        throw new Error('Failed to fetch diagnostic data');
      }

      const diagnostic = diagnosticResponse.data;
      this.diagnosticData = diagnostic;
      
      // Step 1.5: Fetch ranked keywords for the domain (what actually matters for SEO!)
      // Backend now handles pagination and caching
      this.rankedKeywordsMap.clear();
      
      try {
        const rankedResponse = await this.http.post<any>(
          `${API_URL}/dataforseo/keyword-research/ranked`,
          {
            target: this.domain.toLowerCase().trim(),
            locationCode: this.locationCode,
            languageName: this.languageName
            // Backend handles pagination automatically
          }
        ).toPromise();

        // Store metadata about data source
        this.rankedKeywordsMetadata = rankedResponse.metadata || {
          dataSource: 'unknown',
          lastUpdated: null,
          keywordCount: 0
        };

          // Track cost from ranked keywords API (only if fetched from API)
          if (this.rankedKeywordsMetadata && this.rankedKeywordsMetadata.dataSource === 'api' && rankedResponse.data?.tasks?.[0]?.cost) {
            this.totalCost += rankedResponse.data.tasks[0].cost;
          }

          if (rankedResponse.success && rankedResponse.data?.tasks?.[0]?.result?.[0]) {
            const result = rankedResponse.data.tasks[0].result[0];
            const rankedItems = result.items || [];
            
            console.log(`📊 Ranked keywords: ${rankedItems.length} items (source: ${this.rankedKeywordsMetadata?.dataSource || 'unknown'}, updated: ${this.rankedKeywordsMetadata?.lastUpdated || 'N/A'})`);
            
            // Build map of keywords the domain ranks for
            rankedItems.forEach((item: any) => {
              // Structure: item.keyword_data.keyword (not item.keyword)
              const keyword = item.keyword_data?.keyword;
              if (keyword) {
                // Normalize keyword: lowercase, trim, normalize spaces
                const keywordNormalized = keyword.toLowerCase().trim().replace(/\s+/g, ' ');
                
                // Structure: item.ranked_serp_element.serp_item (not item.serp_item)
                const rankedSerpElement = item.ranked_serp_element;
                if (!rankedSerpElement) {
                  return; // Skip items without SERP element
                }
                
                const serpItem = rankedSerpElement?.serp_item;
                if (!serpItem) {
                  return; // Skip items without SERP item
                }
                
                const position = serpItem?.rank_group || serpItem?.rank_absolute || 999;
                const url = serpItem?.url;
                
                // Store the best position for this keyword (if we see it multiple times, keep the best)
                if (!this.rankedKeywordsMap.has(keywordNormalized) || this.rankedKeywordsMap.get(keywordNormalized)!.position > position) {
                  this.rankedKeywordsMap.set(keywordNormalized, {
                    position,
                    serpItem,
                    url
                  });
                }
              }
            });
            
            console.log(`✅ Loaded ${this.rankedKeywordsMap.size} ranked keywords for ${this.domain} (from ${this.rankedKeywordsMetadata?.dataSource || 'unknown'})`);
            // Debug: log first 5 ranked keywords
            const sampleKeywords = Array.from(this.rankedKeywordsMap.keys()).slice(0, 5);
            console.log('📝 Sample ranked keywords:', sampleKeywords);
          } else {
            console.warn('⚠️ No ranked keywords found in response. Success:', rankedResponse.success);
          }
      } catch (error: any) {
        console.warn('⚠️ Failed to fetch ranked keywords, falling back to topicSlug matching:', error.message);
        // Fallback to topicSlug matching if ranked keywords fail
        const allTopicsResponse = await this.http.get<any>(
          `${API_URL}/domains/${this.domain.toLowerCase().trim()}/topics`
        ).toPromise();

        if (allTopicsResponse.success && allTopicsResponse.data) {
          this.topicSlugMap.clear();
          const allParentTopics = allTopicsResponse.data.parentTopics || [];
          
          allParentTopics.forEach((topic: any) => {
            const parentSlug = topic.parentTopicSlug.toLowerCase();
            this.topicSlugMap.set(parentSlug, {
              urlCount: topic.pageCount || 0,
              label: topic.label
            });
            
            if (topic.childTopicSlugs && Array.isArray(topic.childTopicSlugs)) {
              topic.childTopicSlugs.forEach((childSlug: string) => {
                const normalizedChildSlug = childSlug.toLowerCase();
                if (!this.topicSlugMap.has(normalizedChildSlug)) {
                  this.topicSlugMap.set(normalizedChildSlug, {
                    urlCount: topic.pageCount || 0,
                    label: topic.label
                  });
                }
              });
            }
          });
        }
      }
      
      // Extract topics: merged topics + related topics (for the batch search)
      const mergedTopics = diagnostic.coverage?.directlyCoveredTopics || [];
      const relatedTopics = diagnostic.coverage?.relatedTopics || [];
      
      const allTopics = [
        ...mergedTopics.map((t: any) => ({ label: t.label, slug: t.parentTopicSlug, type: 'merged' })),
        ...relatedTopics.map((t: any) => ({ label: t.label, slug: t.parentTopicSlug, type: 'related' }))
      ];

      if (allTopics.length === 0) {
        throw new Error('No topics found for this topic slug');
      }

      // Step 2: For each topic, call DataForSEO (Related Keywords + Keyword Suggestions)
      const results: TopicKeywordResult[] = [];

      for (const topic of allTopics) {
        try {
          // Call Related Keywords
          const relatedBody: any = {
            keyword: topic.label,
            locationCode: this.locationCode,
            languageName: this.languageName,
            depth: this.depth,
            limit: this.limit
          };
          if (this.filterBySearchVolume && this.minSearchVolume > 0) {
            relatedBody.filters = [["keyword_data.keyword_info.search_volume", ">", this.minSearchVolume]];
          }

          const relatedResponse = await this.http.post<any>(
            `${API_URL}/dataforseo/keyword-research/related`,
            relatedBody
          ).toPromise();

          // Track cost from related keywords (only if fetched from API)
          if (relatedResponse.metadata?.dataSource === 'api' && relatedResponse.data?.tasks?.[0]?.cost) {
            this.totalCost += relatedResponse.data.tasks[0].cost;
          }

          // Call Keyword Suggestions
          const suggestionsBody: any = {
            keyword: topic.label,
            locationCode: this.locationCode,
            languageName: this.languageName,
            limit: this.limit
          };
          if (this.filterBySearchVolume && this.minSearchVolume > 0) {
            suggestionsBody.filters = [["keyword_info.search_volume", ">", this.minSearchVolume]];
          }

          const suggestionsResponse = await this.http.post<any>(
            `${API_URL}/dataforseo/keyword-research/suggestions`,
            suggestionsBody
          ).toPromise();

          // Track cost from keyword suggestions (only if fetched from API)
          if (suggestionsResponse.metadata?.dataSource === 'api' && suggestionsResponse.data?.tasks?.[0]?.cost) {
            this.totalCost += suggestionsResponse.data.tasks[0].cost;
          }

          // Parse results
          const relatedKeywords = this.parseKeywordsFromResponse(relatedResponse.data, 'related');
          const suggestions = this.parseKeywordsFromResponse(suggestionsResponse.data, 'suggestions');

          // Capture metadata about data sources
          const relatedKeywordsMetadata = relatedResponse.metadata || {
            dataSource: 'unknown' as const,
            lastUpdated: null
          };
          const suggestionsMetadata = suggestionsResponse.metadata || {
            dataSource: 'unknown' as const,
            lastUpdated: null
          };

          // Add to allKeywords set for deduplication
          relatedKeywords.forEach(k => this.allKeywords.add(k.keyword.toLowerCase()));
          suggestions.forEach(k => this.allKeywords.add(k.keyword.toLowerCase()));

          // Group keywords by core_keyword (seed topic takes precedence)
          const groupedRelatedKeywords = this.groupKeywordsByCore(relatedKeywords, topic.label);
          const groupedSuggestions = this.groupKeywordsByCore(suggestions, topic.label);

          results.push({
            topicSlug: topic.slug,
            topicLabel: topic.label,
            topicType: topic.type,
            relatedKeywords,
            suggestions,
            groupedRelatedKeywords,
            groupedSuggestions,
            relatedKeywordsMetadata,
            suggestionsMetadata
          });
        } catch (error: any) {
          console.error(`Error fetching keywords for topic ${topic.label}:`, error);
          // Continue with other topics even if one fails
        }
      }

      this.batchResults = results;
      
      // Process keywords into deduplicated clusters (async - calculates similarity scores)
      await this.processKeywordsIntoClusters();
    } catch (error: any) {
      console.error('Error in batch search:', error);
      this.batchError = error.error?.message || error.message || 'Failed to search topics for keywords';
    } finally {
      this.batchLoading = false;
    }
  }

  private parseKeywordsFromResponse(data: any, method: 'related' | 'suggestions'): KeywordResult[] {
    if (!data?.tasks?.[0]?.result?.[0]) {
      return [];
    }

    const result = data.tasks[0].result[0];
    const items = result.items || [];

    return items.map((item: any) => {
      const keyword = method === 'related' 
        ? (item.keyword_data?.keyword || item.keyword || '')
        : (item.keyword || '');
      
      // Check if this keyword is covered (PRIMARY: check if domain ranks for it)
      const keywordLower = keyword.toLowerCase().trim().replace(/\s+/g, ' '); // Normalize same way as ranked keywords
      let isCovered = false;
      let urlCount = 0;
      let matchedTopicLabel: string | undefined;
      let rankingPosition: number | undefined;
      let rankingUrl: string | undefined;
      
      // PRIMARY METHOD: Check if domain ranks for this keyword
      // Try exact match first
      if (this.rankedKeywordsMap.has(keywordLower)) {
        const rankingData = this.rankedKeywordsMap.get(keywordLower)!;
        isCovered = true;
        rankingPosition = rankingData.position;
        rankingUrl = rankingData.url;
        urlCount = 1; // At least one page ranks for it
        matchedTopicLabel = keyword; // Use the keyword itself as label
      } else {
        // Try fuzzy matching: check if any ranked keyword contains this keyword or vice versa
        for (const [rankedKeyword, rankingData] of this.rankedKeywordsMap.entries()) {
          // Exact match (already checked above, but just in case)
          if (rankedKeyword === keywordLower) {
            isCovered = true;
            rankingPosition = rankingData.position;
            rankingUrl = rankingData.url;
            urlCount = 1;
            matchedTopicLabel = keyword;
            break;
          }
          // Check if keywords are the same when normalized (spaces/hyphens)
          const rankedNormalized = rankedKeyword.replace(/\s+/g, ' ').replace(/[^a-z0-9\s]/g, '');
          const keywordNormalized = keywordLower.replace(/\s+/g, ' ').replace(/[^a-z0-9\s]/g, '');
          if (rankedNormalized === keywordNormalized) {
            isCovered = true;
            rankingPosition = rankingData.position;
            rankingUrl = rankingData.url;
            urlCount = 1;
            matchedTopicLabel = keyword;
            break;
          }
        }
      }
      
      if (!isCovered) {
        // FALLBACK: Check topicSlug matching (for backwards compatibility if ranked keywords unavailable)
        const normalizedKeyword = keywordLower.replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '');
        
        for (const [topicSlug, topicInfo] of this.topicSlugMap.entries()) {
          const topicSlugLower = topicSlug.toLowerCase();
          const topicLabelLower = topicInfo.label.toLowerCase();
          const topicSlugNormalized = topicSlugLower.replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '');
          
          // Check multiple matching strategies:
          if (keywordLower === topicSlugLower || 
              normalizedKeyword === topicSlugNormalized ||
              keywordLower === topicLabelLower ||
              normalizedKeyword === topicLabelLower.replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '')) {
            isCovered = true;
            urlCount = topicInfo.urlCount;
            matchedTopicLabel = topicInfo.label;
            break;
          }
          
          // Word-based matching
          const keywordWords = keywordLower.split(/\s+/).filter((w: string) => w.length > 2).sort();
          const topicSlugWords = topicSlugLower.split(/-|\s+/).filter((w: string) => w.length > 2).sort();
          if (keywordWords.length > 0 && topicSlugWords.length > 0 && 
              keywordWords.length === topicSlugWords.length &&
              keywordWords.every((w: string) => topicSlugWords.includes(w)) &&
              topicSlugWords.every((w: string) => keywordWords.includes(w))) {
            isCovered = true;
            urlCount = topicInfo.urlCount;
            matchedTopicLabel = topicInfo.label;
            break;
          }
        }
      }

      const baseResult: KeywordResult = {
        keyword: keyword,
        search_volume: method === 'related' 
          ? (item.keyword_data?.keyword_info?.search_volume || 0)
          : (item.keyword_info?.search_volume || 0),
        competition: method === 'related'
          ? (item.keyword_data?.keyword_info?.competition || 0)
          : (item.keyword_info?.competition || 0),
        competition_level: method === 'related'
          ? (item.keyword_data?.keyword_info?.competition_level || 'UNKNOWN')
          : (item.keyword_info?.competition_level || 'UNKNOWN'),
        cpc: method === 'related'
          ? (item.keyword_data?.keyword_info?.cpc || 0)
          : (item.keyword_info?.cpc || 0),
        keyword_difficulty: method === 'related'
          ? item.keyword_data?.keyword_properties?.keyword_difficulty
          : item.keyword_properties?.keyword_difficulty,
        categories: method === 'related'
          ? (item.keyword_data?.keyword_info?.categories || [])
          : (item.keyword_info?.categories || []),
        core_keyword: method === 'related'
          ? item.keyword_data?.keyword_properties?.core_keyword
          : item.keyword_properties?.core_keyword
      };

      // Add coverage info (now includes ranking data)
      return {
        ...baseResult,
        isCovered,
        urlCount,
        matchedTopicLabel,
        rankingPosition,
        rankingUrl
      };
    });
  }

  getTotalUniqueKeywords(): number {
    return this.allKeywords.size;
  }

  toggleGroupExpansion(topicSlug: string, section: 'related' | 'suggestions', groupName: string): void {
    const key = `${topicSlug}-${section}-${groupName}`;
    if (this.expandedGroups.has(key)) {
      this.expandedGroups.delete(key);
    } else {
      this.expandedGroups.add(key);
    }
  }

  isGroupExpanded(topicSlug: string, section: 'related' | 'suggestions', groupName: string): boolean {
    const key = `${topicSlug}-${section}-${groupName}`;
    return this.expandedGroups.has(key);
  }

  /**
   * Group keywords by core_keyword, with seed topic taking precedence
   */
  private groupKeywordsByCore(keywords: KeywordResult[], seedTopic: string): KeywordGroup[] {
    const groups = new Map<string, KeywordResult[]>();
    const seedTopicLower = seedTopic.toLowerCase();
    const seedTopicNormalized = seedTopicLower.replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '');

    // Helper function to check if a string matches seed topic
    // More strict matching - only exact/normalized matches or true synonyms (same words, no extra words)
    const matchesSeedTopic = (str: string): boolean => {
      if (!str) return false;
      const strLower = str.toLowerCase();
      const strNormalized = strLower.replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '');
      
      // Exact matches
      if (strLower === seedTopicLower || strNormalized === seedTopicNormalized) {
        return true;
      }
      
      // Normalized exact match only (catches "feedback-for-websites" vs "website-feedback" when normalized)
      // But NOT "website-feedback-tools" vs "website-feedback" (different concepts)
      if (seedTopicNormalized === strNormalized) {
        return true;
      }
      
      // Word-based matching: ONLY if they have EXACTLY the same words (order-independent, no extra words)
      // This catches "feedback for websites" vs "website feedback" but NOT "website feedback tools" vs "website feedback"
      const seedWords = seedTopicLower.split(/\s+/).filter(w => w.length > 2).sort();
      const strWords = strLower.split(/\s+/).filter(w => w.length > 2).sort();
      
      if (seedWords.length > 0 && strWords.length > 0 && seedWords.length === strWords.length) {
        // Same number of words and all words match (order-independent)
        const allMatch = seedWords.every(word => strWords.includes(word)) && 
                        strWords.every(word => seedWords.includes(word));
        if (allMatch) {
          return true;
        }
      }
      
      return false;
    };

    for (const keyword of keywords) {
      let groupName: string;

      // Priority 1: If keyword itself matches seed topic, ALWAYS use seed topic
      if (matchesSeedTopic(keyword.keyword)) {
        groupName = seedTopic;
      }
      // Priority 2: If core_keyword matches seed topic, use seed topic
      else if (keyword.core_keyword && matchesSeedTopic(keyword.core_keyword)) {
        groupName = seedTopic;
      }
      // Priority 3: Use core_keyword if it exists (for other groups)
      else if (keyword.core_keyword) {
        groupName = keyword.core_keyword;
      }
      // Priority 4: Use keyword itself (singleton group)
      else {
        groupName = keyword.keyword;
      }

      // Add to group
      if (!groups.has(groupName)) {
        groups.set(groupName, []);
      }
      groups.get(groupName)!.push(keyword);
    }

    // Convert to KeywordGroup array with aggregated metrics
    const keywordGroups: KeywordGroup[] = [];
    for (const [groupName, groupKeywords] of groups.entries()) {
      const totalSearchVolume = groupKeywords.reduce((sum, k) => sum + k.search_volume, 0);
      const totalUrlCount = groupKeywords.reduce((sum, k) => sum + (k.urlCount || 0), 0);
      const coveredCount = groupKeywords.filter(k => k.isCovered).length;
      const isCovered = coveredCount > 0;

      keywordGroups.push({
        groupName,
        keywords: groupKeywords,
        totalSearchVolume,
        totalUrlCount,
        isCovered,
        coveredCount,
        totalCount: groupKeywords.length
      });
    }

    // Sort by total search volume (descending)
    return keywordGroups.sort((a, b) => b.totalSearchVolume - a.totalSearchVolume);
  }

  /**
   * Normalize a keyword for deduplication
   * Removes stop words, normalizes word order, and standardizes formatting
   */
  private normalizeKeywordForDeduplication(keyword: string): string {
    if (!keyword) return '';
    
    const lower = keyword.toLowerCase().trim();
    
    // Common stop words to remove
    const stopWords = new Set(['a', 'an', 'and', 'are', 'as', 'at', 'be', 'by', 'for', 'from', 
      'has', 'he', 'in', 'is', 'it', 'its', 'of', 'on', 'that', 'the', 'to', 'was', 'will', 'with']);
    
    // Split into words, remove stop words, filter out empty strings
    const words = lower
      .split(/[\s\-_]+/)
      .map(w => w.replace(/[^a-z0-9]/g, ''))
      .filter(w => w.length > 0 && !stopWords.has(w))
      .sort(); // Sort words alphabetically for consistent ordering
    
    // Join back together
    return words.join(' ');
  }

  /**
   * Deduplicate keywords across all topics
   * Groups similar keywords (e.g., "website feedback", "feedback website", "feedback on website") together
   */
  private deduplicateKeywords(allKeywords: KeywordResult[]): Map<string, DeduplicatedKeyword> {
    const deduplicatedMap = new Map<string, DeduplicatedKeyword>();
    
    for (const keyword of allKeywords) {
      const normalized = this.normalizeKeywordForDeduplication(keyword.keyword);
      
      if (!normalized) continue; // Skip empty keywords
      
      if (!deduplicatedMap.has(normalized)) {
        // Use the original keyword with highest search volume as canonical form
        // For now, use the first one, we'll update it later
        deduplicatedMap.set(normalized, {
          canonicalKeyword: keyword.keyword,
          variants: [],
          totalSearchVolume: 0,
          totalUrlCount: 0,
          isCovered: false,
          coveredCount: 0
        });
      }
      
      const deduped = deduplicatedMap.get(normalized)!;
      deduped.variants.push(keyword);
      deduped.totalSearchVolume += keyword.search_volume;
      deduped.totalUrlCount += (keyword.urlCount || 0);
      
      if (keyword.isCovered) {
        deduped.isCovered = true;
        deduped.coveredCount++;
        
        // Track best ranking position
        if (keyword.rankingPosition !== undefined) {
          if (deduped.bestRankingPosition === undefined || 
              keyword.rankingPosition < deduped.bestRankingPosition) {
            deduped.bestRankingPosition = keyword.rankingPosition;
            deduped.bestRankingUrl = keyword.rankingUrl;
          }
        }
      }
    }
    
    // Update canonical keyword to be the one with highest search volume
    for (const deduped of deduplicatedMap.values()) {
      deduped.variants.sort((a, b) => b.search_volume - a.search_volume);
      deduped.canonicalKeyword = deduped.variants[0].keyword;
    }
    
    return deduplicatedMap;
  }


  /**
   * Cluster deduplicated keywords into their best clusters
   * Each keyword appears in only one cluster
   */
  private clusterDeduplicatedKeywords(
    deduplicatedKeywords: Map<string, DeduplicatedKeyword>,
    seedTopics: string[]
  ): KeywordCluster[] {
    const clusters = new Map<string, DeduplicatedKeyword[]>();
    const assignedKeywords = new Set<string>(); // Track which keywords have been assigned
    
    // First pass: assign keywords to seed topics if they match
    const seedTopicsLower = seedTopics.map(t => t.toLowerCase().trim());
    
    for (const [normalized, deduped] of deduplicatedKeywords.entries()) {
      if (assignedKeywords.has(normalized)) continue;
      
      const canonicalLower = deduped.canonicalKeyword.toLowerCase();
      const canonicalNormalized = this.normalizeKeywordForDeduplication(deduped.canonicalKeyword);
      
      // Check if it matches any seed topic
      let matchedTopic: string | null = null;
      for (const seedTopic of seedTopics) {
        const seedLower = seedTopic.toLowerCase();
        const seedNormalized = this.normalizeKeywordForDeduplication(seedTopic);
        
        // Exact match or normalized match
        if (canonicalLower === seedLower || canonicalNormalized === seedNormalized) {
          matchedTopic = seedTopic;
          break;
        }
        
        // Check if canonical keyword contains seed topic or vice versa
        if (canonicalNormalized.includes(seedNormalized) || seedNormalized.includes(canonicalNormalized)) {
          // Prefer exact matches, but allow partial matches if no exact match found
          if (!matchedTopic) {
            matchedTopic = seedTopic;
          }
        }
      }
      
      if (matchedTopic) {
        if (!clusters.has(matchedTopic)) {
          clusters.set(matchedTopic, []);
        }
        clusters.get(matchedTopic)!.push(deduped);
        assignedKeywords.add(normalized);
      }
    }
    
    // Second pass: assign remaining keywords to best matching cluster
    for (const [normalized, deduped] of deduplicatedKeywords.entries()) {
      if (assignedKeywords.has(normalized)) continue;
      
      // Find the best matching cluster
      let bestCluster: string | null = null;
      let bestScore = 0;
      
      for (const clusterName of clusters.keys()) {
        const clusterNormalized = this.normalizeKeywordForDeduplication(clusterName);
        const keywordNormalized = this.normalizeKeywordForDeduplication(deduped.canonicalKeyword);
        
        // Calculate similarity score (simple word overlap)
        const clusterWords = new Set(clusterNormalized.split(' '));
        const keywordWords = new Set(keywordNormalized.split(' '));
        
        let commonWords = 0;
        let totalWords = 0;
        
        for (const word of keywordWords) {
          if (clusterWords.has(word)) {
            commonWords++;
          }
          totalWords++;
        }
        
        const score = totalWords > 0 ? commonWords / totalWords : 0;
        
        if (score > bestScore && score > 0.5) { // Require at least 50% word overlap
          bestScore = score;
          bestCluster = clusterName;
        }
      }
      
      // If no good match found, create a new cluster with the canonical keyword
      if (!bestCluster) {
        bestCluster = deduped.canonicalKeyword;
        clusters.set(bestCluster, []);
      }
      
      clusters.get(bestCluster)!.push(deduped);
      assignedKeywords.add(normalized);
    }
    
    // Convert to KeywordCluster array
    const keywordClusters: KeywordCluster[] = [];
    for (const [clusterName, keywords] of clusters.entries()) {
      const totalSearchVolume = keywords.reduce((sum, k) => sum + k.totalSearchVolume, 0);
      const totalUrlCount = keywords.reduce((sum, k) => sum + k.totalUrlCount, 0);
      const coveredCount = keywords.filter(k => k.isCovered).length;
      const isCovered = coveredCount > 0;
      const totalKeywordVariants = keywords.reduce((sum, k) => sum + k.variants.length, 0);
      
      keywordClusters.push({
        clusterName,
        keywords: keywords.sort((a, b) => b.totalSearchVolume - a.totalSearchVolume),
        totalSearchVolume,
        totalUrlCount,
        isCovered,
        coveredCount,
        totalKeywordVariants,
        similarityScore: undefined // Will be calculated after we have the main topic slug
      });
    }
    
    // Sort by total search volume (descending)
    return keywordClusters.sort((a, b) => b.totalSearchVolume - a.totalSearchVolume);
  }

  /**
   * Process all keywords from batch results into deduplicated clusters
   */
  async processKeywordsIntoClusters(): Promise<void> {
    // Collect all keywords from all topics
    const allKeywords: KeywordResult[] = [];
    const seedTopics: string[] = [];
    
    for (const topicResult of this.batchResults) {
      // Add seed topic
      seedTopics.push(topicResult.topicLabel);
      
      // Add all keywords
      allKeywords.push(...topicResult.relatedKeywords);
      allKeywords.push(...topicResult.suggestions);
    }
    
    // Deduplicate keywords
    const deduplicated = this.deduplicateKeywords(allKeywords);
    
    // Cluster deduplicated keywords
    this.clusteredKeywords = this.clusterDeduplicatedKeywords(deduplicated, seedTopics);
    
    // Initialize filtered clusters (will be updated after similarity calculation)
    this.filteredClusteredKeywords = [];
    
    // Calculate similarity scores to main topic slug using embeddings
    const mainTopicSlug = this.topicSlug.trim();
    if (mainTopicSlug && this.clusteredKeywords.length > 0) {
      await this.calculateSimilarityScores(mainTopicSlug);
    } else {
      // If no topic slug, show all clusters (no filtering)
      this.filteredClusteredKeywords = this.clusteredKeywords;
    }
    
    console.log(`✅ Processed ${allKeywords.length} raw keywords → ${deduplicated.size} deduplicated keywords → ${this.clusteredKeywords.length} clusters`);
    console.log(`   Unique keyword strings: ${this.allKeywords.size}`);
    console.log(`   Deduplicated (normalized): ${deduplicated.size}`);
    console.log(`   Final clusters: ${this.clusteredKeywords.length}`);
    if (mainTopicSlug) {
      console.log(`   Similarity scores calculated against: "${mainTopicSlug}"`);
      console.log(`   Filtered to ${this.filteredClusteredKeywords.length} clusters with similarity >= ${(this.similarityThreshold * 100).toFixed(0)}%`);
    }
  }

  toggleClusterExpansion(clusterName: string): void {
    if (this.expandedClusters.has(clusterName)) {
      this.expandedClusters.delete(clusterName);
    } else {
      this.expandedClusters.add(clusterName);
    }
  }

  isClusterExpanded(clusterName: string): boolean {
    return this.expandedClusters.has(clusterName);
  }

  /**
   * Calculate similarity scores using OpenAI embeddings via backend API
   */
  private async calculateSimilarityScores(topicSlug: string): Promise<void> {
    try {
      const API_URL = environment.apiUrl + '/v1';
      const clusterNames = this.clusteredKeywords.map(c => c.clusterName);
      
      const response = await this.http.post<any>(
        `${API_URL}/dataforseo/keyword-research/calculate-similarity`,
        {
          topicSlug,
          clusterNames
        }
      ).toPromise();
      
      if (response.success && response.data?.similarityScores) {
        const similarityScores = response.data.similarityScores;
        
        // Assign similarity scores to clusters
        for (const cluster of this.clusteredKeywords) {
          cluster.similarityScore = similarityScores[cluster.clusterName] || 0;
        }
        
        // Re-sort by similarity score (descending), then by search volume
        this.clusteredKeywords.sort((a, b) => {
          const scoreA = a.similarityScore || 0;
          const scoreB = b.similarityScore || 0;
          if (Math.abs(scoreA - scoreB) > 0.01) {
            return scoreB - scoreA; // Higher similarity first
          }
          return b.totalSearchVolume - a.totalSearchVolume; // Then by search volume
        });
        
        // First, deduplicate clusters with exact same names (shouldn't happen, but just in case)
        this.deduplicateExactClusterNames();
        
        // Then merge clusters that are very similar to each other
        await this.mergeSimilarClusters();
        
        // Filter clusters by similarity threshold
        this.updateFilteredClusters();
        
        console.log(`✅ Calculated similarity scores for ${this.clusteredKeywords.length} clusters using embeddings`);
        console.log(`   Filtered to ${this.filteredClusteredKeywords.length} clusters with similarity >= ${(this.similarityThreshold * 100).toFixed(0)}%`);
      } else {
        console.warn('⚠️  Failed to calculate similarity scores, falling back to 0');
        // Set all scores to 0 if API call fails
        for (const cluster of this.clusteredKeywords) {
          cluster.similarityScore = 0;
        }
      }
    } catch (error: any) {
      console.error('Error calculating similarity scores:', error);
      // Set all scores to 0 if API call fails
      for (const cluster of this.clusteredKeywords) {
        cluster.similarityScore = 0;
      }
      this.updateFilteredClusters();
    }
  }

  /**
   * Deduplicate clusters with exact same names (shouldn't happen, but safety check)
   */
  private deduplicateExactClusterNames(): void {
    const seenNames = new Map<string, number>();
    const deduplicated: KeywordCluster[] = [];
    
    for (const cluster of this.clusteredKeywords) {
      const normalizedName = cluster.clusterName.toLowerCase().trim();
      
      if (seenNames.has(normalizedName)) {
        // Merge into existing cluster
        const existingIndex = seenNames.get(normalizedName)!;
        const existingCluster = deduplicated[existingIndex];
        
        existingCluster.keywords.push(...cluster.keywords);
        existingCluster.totalSearchVolume += cluster.totalSearchVolume;
        existingCluster.totalUrlCount += cluster.totalUrlCount;
        existingCluster.totalKeywordVariants += cluster.totalKeywordVariants;
        existingCluster.coveredCount += cluster.coveredCount;
        existingCluster.isCovered = existingCluster.isCovered || cluster.isCovered;
        
        // Keep the higher similarity score
        if ((cluster.similarityScore || 0) > (existingCluster.similarityScore || 0)) {
          existingCluster.similarityScore = cluster.similarityScore;
        }
        
        // Sort keywords by search volume
        existingCluster.keywords.sort((a, b) => b.totalSearchVolume - a.totalSearchVolume);
      } else {
        // First time seeing this name
        seenNames.set(normalizedName, deduplicated.length);
        deduplicated.push(cluster);
      }
    }
    
    if (deduplicated.length < this.clusteredKeywords.length) {
      console.log(`🔧 Deduplicated ${this.clusteredKeywords.length - deduplicated.length} clusters with exact same names`);
      this.clusteredKeywords = deduplicated;
    }
  }

  /**
   * Merge clusters that are very similar to each other (e.g., "website feedback tool" vs "website feedback tools")
   * Uses embeddings to calculate inter-cluster similarity
   */
  private async mergeSimilarClusters(): Promise<void> {
    if (this.clusteredKeywords.length <= 1) return;
    
    const mergeThreshold = 0.85; // Merge clusters with >85% similarity to each other
    const clustersToMerge = new Map<number, number>(); // Map: cluster index → merge into this index
    const merged = new Set<number>();
    
    // Calculate pairwise similarities between clusters
    const clusterNames = this.clusteredKeywords.map(c => c.clusterName);
    
    try {
      const API_URL = environment.apiUrl + '/v1';
      
      // Calculate all pairwise similarities in one batch call
      const response = await this.http.post<any>(
        `${API_URL}/dataforseo/keyword-research/calculate-pairwise-similarity`,
        {
          clusterNames
        }
      ).toPromise();
      
      if (response.success && response.data?.pairwiseSimilarities) {
        const pairwiseSimilarities = response.data.pairwiseSimilarities;
        
        // Find clusters to merge
        for (let i = 0; i < clusterNames.length; i++) {
          if (merged.has(i)) continue;
          
          const cluster1 = clusterNames[i];
          const similarities = pairwiseSimilarities[cluster1] || {};
          
          for (let j = i + 1; j < clusterNames.length; j++) {
            if (merged.has(j)) continue;
            
            const cluster2 = clusterNames[j];
            const similarity = similarities[cluster2] || 0;
            
            if (similarity >= mergeThreshold) {
              // Decide which cluster to keep (prefer higher similarity to main topic, then higher search volume)
              const clusterI = this.clusteredKeywords[i];
              const clusterJ = this.clusteredKeywords[j];
              
              const scoreI = clusterI.similarityScore || 0;
              const scoreJ = clusterJ.similarityScore || 0;
              
              let keepIndex: number;
              let mergeIndex: number;
              
              if (Math.abs(scoreI - scoreJ) > 0.01) {
                // Keep the one with higher similarity to main topic
                keepIndex = scoreI > scoreJ ? i : j;
                mergeIndex = scoreI > scoreJ ? j : i;
              } else {
                // If similarity scores are close, keep the one with higher search volume
                keepIndex = clusterI.totalSearchVolume > clusterJ.totalSearchVolume ? i : j;
                mergeIndex = clusterI.totalSearchVolume > clusterJ.totalSearchVolume ? j : i;
              }
              
              clustersToMerge.set(mergeIndex, keepIndex);
              merged.add(mergeIndex);
            }
          }
        }
      }
      
      // Perform merges
      if (clustersToMerge.size > 0) {
        // First, resolve transitive merges (if A merges into B, and B merges into C, then A should merge into C)
        const resolvedMerges = new Map<number, number>();
        for (const [sourceIndex, targetIndex] of clustersToMerge.entries()) {
          let finalTarget = targetIndex;
          // Follow the chain to find the ultimate target
          while (clustersToMerge.has(finalTarget)) {
            finalTarget = clustersToMerge.get(finalTarget)!;
          }
          resolvedMerges.set(sourceIndex, finalTarget);
        }
        
        // Build merge groups: target index -> array of source indices to merge into it
        const mergeGroups = new Map<number, number[]>();
        for (const [sourceIndex, targetIndex] of resolvedMerges.entries()) {
          if (!mergeGroups.has(targetIndex)) {
            mergeGroups.set(targetIndex, []);
          }
          mergeGroups.get(targetIndex)!.push(sourceIndex);
        }
        
        // Perform the merges
        const mergedClusters: KeywordCluster[] = [];
        const processedIndices = new Set<number>();
        
        for (let i = 0; i < this.clusteredKeywords.length; i++) {
          if (processedIndices.has(i)) continue;
          
          if (mergeGroups.has(i)) {
            // This is a target cluster - merge all sources into it
            const targetCluster = this.clusteredKeywords[i];
            const sourcesToMerge = mergeGroups.get(i)!;
            
            for (const sourceIndex of sourcesToMerge) {
              const sourceCluster = this.clusteredKeywords[sourceIndex];
              
              // Merge source into target
              targetCluster.keywords.push(...sourceCluster.keywords);
              targetCluster.totalSearchVolume += sourceCluster.totalSearchVolume;
              targetCluster.totalUrlCount += sourceCluster.totalUrlCount;
              targetCluster.totalKeywordVariants += sourceCluster.totalKeywordVariants;
              targetCluster.coveredCount += sourceCluster.coveredCount;
              targetCluster.isCovered = targetCluster.isCovered || sourceCluster.isCovered;
              
              processedIndices.add(sourceIndex);
            }
            
            // Sort keywords by search volume
            targetCluster.keywords.sort((a, b) => b.totalSearchVolume - a.totalSearchVolume);
            
            mergedClusters.push(targetCluster);
            processedIndices.add(i);
          } else if (!resolvedMerges.has(i)) {
            // This cluster is not being merged and is not a source - keep it as-is
            mergedClusters.push(this.clusteredKeywords[i]);
            processedIndices.add(i);
          }
          // If it's in resolvedMerges but not a target, it's a source that will be merged, so skip it
        }
        
        this.clusteredKeywords = mergedClusters;
        console.log(`✅ Merged ${clustersToMerge.size} clusters into existing clusters (similarity >= ${(mergeThreshold * 100).toFixed(0)}%)`);
        console.log(`   Final cluster count: ${mergedClusters.length} (down from ${this.clusteredKeywords.length + clustersToMerge.size})`);
      }
    } catch (error: any) {
      console.warn('⚠️  Failed to merge similar clusters:', error.message);
      // Continue without merging if API call fails
    }
  }

  /**
   * Update filtered clusters based on similarity threshold
   */
  private updateFilteredClusters(): void {
    this.filteredClusteredKeywords = this.clusteredKeywords.filter(
      cluster => (cluster.similarityScore || 0) >= this.similarityThreshold
    );
  }
}

